import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

interface LotteryEntry {
  entryId: string;
  tagId: string;
  campaignId: string;
  campaignName: string;
  status: "pending" | "winner" | "loser";
  prizeWon?: string;
  enteredAt: string;
  drawDate?: string;
}

const MyEntriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LotteryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMyEntries();
  }, []);

  const loadMyEntries = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🔑 Retrieve token from localStorage
      const token =
        localStorage.getItem("consumerToken") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("token");

      if (!token) {
        setError("Please log in to view your lottery entries.");
        setLoading(false);
        return;
      }

      console.log("📋 Loading entries for authenticated consumer...");

      // ✅ Correct backend endpoint
      const response = await fetch(`${API_BASE}/consumer-lottery/my-entries`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Session expired. Please log in again.");
        }
        throw new Error("Failed to load lottery entries.");
      }

      const data = await response.json();
      console.log("📋 Entries response:", data);

      if (data.success && data.entries) {
        // ✅ Map backend entries to UI structure
        const mappedEntries = data.entries.map((entry: any) => ({
          entryId: entry.entryId || entry._id, // fallback to _id if entryId missing
          tagId: entry.tagId,
          campaignId: entry.lottery?.lotteryId || "",
          campaignName: entry.lottery?.name || "Lottery Campaign",
          status:
            entry.status === "winner" || entry.status === "claimed"
              ? "winner"
              : entry.status === "loser"
              ? "loser"
              : "pending",
          prizeWon: entry.prize?.name
            ? `${entry.prize.name} ($${entry.prize.value})`
            : undefined,
          enteredAt: entry.joinedAt,
          drawDate: entry.lottery?.drawDate,
        }));

        console.log("✅ Mapped entries:", mappedEntries);
        setEntries(mappedEntries);
      } else {
        setEntries([]);
      }
    } catch (err: any) {
      console.error("❌ Error loading entries:", err);
      setError(err.message || "Failed to load your lottery entries.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "winner":
        return "bg-green-100 text-green-800 border-green-300";
      case "loser":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "winner":
        return "🎉";
      case "loser":
        return "😔";
      default:
        return "⏳";
    }
  };

  // 🔄 Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your entries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 text-purple-600 hover:text-purple-700 font-medium flex items-center mx-auto"
          >
            ← Back
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🎫 My Lottery Entries
          </h1>
          <p className="text-gray-600">
            View all your lottery participations and results
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate("/consumer")}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Scan a Tag
            </button>
          </div>
        )}

        {/* Stats Summary */}
        {entries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-md border-2 border-purple-100">
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {entries.length}
              </div>
              <div className="text-gray-600">Total Entries</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md border-2 border-green-100">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {entries.filter((e) => e.status === "winner").length}
              </div>
              <div className="text-gray-600">Wins</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md border-2 border-yellow-100">
              <div className="text-3xl font-bold text-yellow-600 mb-1">
                {entries.filter((e) => e.status === "pending").length}
              </div>
              <div className="text-gray-600">Pending</div>
            </div>
          </div>
        )}

        {/* Entries List */}
        {entries.length > 0 ? (
          <div className="space-y-4">
            {entries.map((entry) => (
              <div
                key={entry.entryId}
                className="bg-white rounded-xl shadow-md border-2 border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {entry.campaignName || "Lottery Campaign"}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Tag ID: <span className="font-mono">{entry.tagId}</span>
                      </p>
                    </div>
                    <span
                      className={`px-4 py-2 rounded-full text-sm font-semibold border-2 ${getStatusColor(
                        entry.status
                      )}`}
                    >
                      {getStatusIcon(entry.status)} {entry.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                    <div>
                      <span className="font-medium">Entered:</span>
                      <p>{new Date(entry.enteredAt).toLocaleDateString()}</p>
                    </div>
                    {entry.drawDate && (
                      <div>
                        <span className="font-medium">Draw Date:</span>
                        <p>{new Date(entry.drawDate).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>

                  {entry.status === "winner" && entry.prizeWon && (
                    <div className="bg-gradient-to-r from-yellow-50 to-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">🏆</span>
                        <div>
                          <div className="font-semibold text-green-800">
                            Prize Won:
                          </div>
                          <div className="text-lg font-bold text-green-900">
                            {entry.prizeWon}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    {/* ✅ FIXED: Use entryId to view details */}
                    <button
                      onClick={() =>
                        navigate(`/consumer/lottery-result/${entry.entryId}`)
                      }
                      className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      View Details
                    </button>
                    {entry.status === "winner" && (
                      <button
                        onClick={() =>
                          navigate(`/consumer/certificate/${entry.tagId}`)
                        }
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        View Certificate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !error && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <div className="text-6xl mb-4">🎰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                No Entries Yet
              </h3>
              <p className="text-gray-600 mb-6">
                Start scanning tags to participate in lotteries!
              </p>
              <button
                onClick={() => navigate("/consumer")}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Scan a Tag Now
              </button>
            </div>
          )
        )}

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="font-semibold text-blue-900 mb-2">📌 How It Works</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Scan authentic product tags to enter lotteries</li>
            <li>• Each tag gives you one entry into the campaign</li>
            <li>• Winners are selected randomly or pre-determined by the brand</li>
            <li>• Check back here to see if you've won!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MyEntriesPage;
